import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { BonkerLogo } from "@/components/BonkerLogo";

const COVER =
  "https://images.pexels.com/photos/29348672/pexels-photo-29348672.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState<string | null>(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setLogo(data.logo_url)).catch(() => {});
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Accesso effettuato");
      navigate(user.must_change_password ? "/cms/change-password" : "/cms", { replace: true });
    } catch (err: any) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Cover */}
      <div className="relative hidden md:block overflow-hidden">
        <img src={COVER} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <BonkerLogo src={logo ? mediaUrl(logo) : null} className="h-10" />
          <div>
            <p className="text-secondary text-xs uppercase tracking-[0.3em] font-bold mb-4">
              Sneaker · Custom · Urban
            </p>
            <h1 className="font-display font-black tracking-tighter text-5xl lg:text-6xl leading-[0.9]">
              PANNELLO
              <br />
              <span className="text-primary">DI CONTROLLO</span>
            </h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="relative flex items-center justify-center p-8 sm:p-12 lg:p-20 bonker-noise">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="w-full max-w-md relative z-10"
        >
          <div className="md:hidden mb-10">
            <BonkerLogo src={logo ? mediaUrl(logo) : null} className="h-10" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">
            Area riservata
          </p>
          <h2 className="font-display font-bold text-3xl tracking-tight mb-10">Accedi al CMS</h2>

          <form onSubmit={submit} className="space-y-6" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Email
              </label>
              <div className="mt-2 flex items-center border border-input bg-black/50 px-4 focus-within:border-primary transition-colors">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="login-email-input"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@bonker.local"
                  autoComplete="username"
                  required
                  className="w-full bg-transparent py-3 px-3 text-foreground placeholder:text-muted-foreground/50 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Password
              </label>
              <div className="mt-2 flex items-center border border-input bg-black/50 px-4 focus-within:border-primary transition-colors">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  className="w-full bg-transparent py-3 px-3 text-foreground placeholder:text-muted-foreground/50 outline-none"
                />
              </div>
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="group w-full bg-primary text-primary-foreground font-bold uppercase tracking-[0.15em] text-sm py-4 flex items-center justify-center gap-2 transition-transform duration-200 hover:-translate-y-[2px] disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Entra
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
